import {
  ChartDimensions,
  Rect,
  getRectCenter,
  inflate,
  intersects,
  shiftInto,
} from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import { createFifoCache, isTruthy } from '@ardoq/common-helpers';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { fontFamily } from '@ardoq/design-tokens';
import {
  applyInteractiveStatesToDataPoint,
  getBoundingBox,
} from './calculations';
import {
  BubbleChartBubbleModel,
  BubbleChartDataPointModel,
  BubbleChartLabelData,
  BubbleChartScalers,
  BubbleChartViewSettings,
  BubbleLayoutInfo,
  DIMENSION_TO_DATA_KEY,
  HighlightState,
  LabelFilter,
} from './types';
import { DISPLAYED_LABELS_LIMIT } from './consts';
import { decollide, MIN_MARGIN } from './bubbleChartAntiCollision';
import { isLabelFilterValid } from './labelFilterValidation';

const getMeasurerForSize = createFifoCache(
  DISPLAYED_LABELS_LIMIT,
  (fontSize: number) =>
    canvasLabelMeasurer(`400 ${fontSize}px/${fontSize}px ${fontFamily.regular}`)
);

const isLabelFilteredOut = (
  dataPoint: BubbleChartDataPointModel,
  labelFilter: LabelFilter
): boolean => {
  const dataKey = DIMENSION_TO_DATA_KEY[labelFilter.dimension];
  const dimensionValue = dataPoint[dataKey];
  return dimensionValue < labelFilter.min || dimensionValue > labelFilter.max;
};

const getLabelRect = (label: BubbleChartBubbleModel) => {
  const roundedFontSize = Math.round(label.fontSize * 100) / 100;
  const width = getMeasurerForSize(roundedFontSize)(label.label);
  return new Rect(
    label.x - width / 2,
    label.y - label.fontSize / 2,
    width,
    label.fontSize
  );
};
const positionLabels = (
  dimensions: ChartDimensions,
  dataPoints: BubbleChartBubbleModel[]
) => {
  const items = [...dataPoints].map<BubbleLayoutInfo>((bubble, index) => ({
    cid: bubble.cid,
    uniqueId: bubble.uniqueId,
    center: [bubble.x, bubble.y],
    radius: bubble.radius,
    labelRect:
      index < DISPLAYED_LABELS_LIMIT
        ? getLabelRect(dataPoints[index])
        : Rect.IMPOSSIBLE,
    index,
    fontSize: index < DISPLAYED_LABELS_LIMIT ? dataPoints[index].fontSize : NaN,
  }));

  const bubblesToDecollide = items.slice(0, DISPLAYED_LABELS_LIMIT);
  const remainingBubbles = items.slice(DISPLAYED_LABELS_LIMIT, items.length);

  const decollidedBubbles = decollide({
    input: bubblesToDecollide,
    center: [dimensions.chart.width / 2, dimensions.chart.height / 2],
    bounds: new Rect(0, 0, dimensions.chart.width, dimensions.chart.height),
  });
  return new Map(
    [...decollidedBubbles, ...remainingBubbles].map(bubble => [
      bubble.uniqueId,
      bubble,
    ])
  );
};

type GetDecollidedDataPointLabelsArgs = {
  positionedDataPoints: BubbleChartBubbleModel[];
  dimensions: ChartDimensions;
};
export const getDecollidedDataPointLabels = ({
  positionedDataPoints,
  dimensions,
}: GetDecollidedDataPointLabelsArgs): BubbleChartLabelData[] => {
  const visibleDataPointLabels = positionedDataPoints.filter(
    point => !point.isLabelHidden
  );

  const layoutData = positionLabels(dimensions, visibleDataPointLabels);

  return visibleDataPointLabels
    .slice(0, DISPLAYED_LABELS_LIMIT)
    .map(dataPoint => {
      const itemLayout = layoutData && layoutData.get(dataPoint.uniqueId);
      const [labelCenterX, labelCenterY] = itemLayout
        ? getRectCenter(itemLayout.labelRect)
        : [dataPoint.x, dataPoint.y];
      return { ...dataPoint, ...{ x: labelCenterX, y: labelCenterY } };
    })
    .filter(label => !isNaN(label.x));
};

type SizeDataPointsArgs = {
  data: BubbleChartDataPointModel[];
  scalers: BubbleChartScalers;
  viewSettings: BubbleChartViewSettings;
  isViewpointMode: boolean;
};
export const sizeDataPoints = ({
  data,
  scalers,
  viewSettings,
  isViewpointMode,
}: SizeDataPointsArgs): BubbleChartBubbleModel[] =>
  data.map((dataPoint, index) => {
    const isLabelHidden =
      isViewpointMode &&
      viewSettings.labelFilter &&
      isLabelFilterValid(viewSettings.labelFilter, viewSettings)
        ? isLabelFilteredOut(dataPoint, viewSettings.labelFilter)
        : false;

    return {
      ...dataPoint,
      radius: scalers.radius(dataPoint.radius),
      fontSize: scalers.fontSize(dataPoint.radius),
      fontOpacity: scalers.fontOpacity(Math.min(100, data.length) - index - 1),
      isBlurred: false,
      isLabelHidden,
    };
  });

type PositionAndCullDataPointsArgs = {
  sizedDataPoints: BubbleChartBubbleModel[];
  scalers: BubbleChartScalers;
  dimensions: ChartDimensions;
  showBubblesWithZeroValue: boolean;
};
export const positionAndCullDataPoints = ({
  sizedDataPoints,
  scalers,
  dimensions,
  showBubblesWithZeroValue,
}: PositionAndCullDataPointsArgs): BubbleChartBubbleModel[] => {
  const chartBounds = new Rect(
    0,
    0,
    dimensions.chart.width,
    dimensions.chart.height
  );

  return sizedDataPoints
    .map(sizedDataPoint => ({
      ...sizedDataPoint,
      x: scalers.x(sizedDataPoint.x),
      y: scalers.y(sizedDataPoint.y),
    }))
    .filter(
      scaledDataPoint =>
        (showBubblesWithZeroValue || scaledDataPoint.radius) &&
        intersects(chartBounds, getBoundingBox(scaledDataPoint))
    );
};

type GetLabelsForHighlightedItemArgs = {
  higlightedItemsToLabels: Map<string, Point | null>;
  highlightDataPoint: BubbleChartBubbleModel;
  chartBounds: Rect;
};
/**
 *  When the item is hovered over and/or focused, we need to display 2 labels for the same item and make one of them transparent.
 * If we just move one label, the highlight area stops to point to the highlighted item and the chart updates hover and/or focus state.
 */
const getLabelsForHighlightedItem = ({
  higlightedItemsToLabels,
  highlightDataPoint,
  chartBounds,
}: GetLabelsForHighlightedItemArgs): BubbleChartLabelData[] => {
  const highlightLabelPosition = higlightedItemsToLabels.get(
    highlightDataPoint.uniqueId
  );

  const [highlightedLabelX, highlightedLabelY] = highlightLabelPosition ?? [];

  const decollidedHighlightedLabel =
    highlightedLabelX !== undefined &&
    highlightedLabelY !== undefined &&
    !(
      highlightedLabelY === highlightDataPoint.y &&
      highlightedLabelX === highlightDataPoint.x
    )
      ? {
          ...highlightDataPoint,
          x: highlightedLabelX,
          y: highlightedLabelY,
          fontOpacity: 0,
          key: highlightDataPoint.uniqueId,
        }
      : null;

  if (decollidedHighlightedLabel) {
    const [newCenterX, newCenterY] = getRectCenter(
      shiftInto(
        getLabelRect(decollidedHighlightedLabel),
        inflate(chartBounds, -MIN_MARGIN)
      )
    );
    decollidedHighlightedLabel.x = newCenterX;
    decollidedHighlightedLabel.y = newCenterY;
  }

  const labelOnHighlightedDataPoint = {
    ...highlightDataPoint,
    fontOpacity: 1,
    key: `${highlightDataPoint.uniqueId}_2`,
  };

  return [labelOnHighlightedDataPoint, decollidedHighlightedLabel].filter(
    isTruthy
  );
};

type InteractiveStates = {
  hoverState: HighlightState;
  focusState: HighlightState;
};
type ApplyInteractiveStatesToDataArgs = InteractiveStates & {
  dataPoints: BubbleChartBubbleModel[];
  labels: BubbleChartLabelData[];
  dimensions: ChartDimensions;
};
export const applyInteractiveStatesToData = ({
  dataPoints,
  labels,
  hoverState,
  focusState,
  dimensions,
}: ApplyInteractiveStatesToDataArgs) => {
  const higlightedItemsToLabels = new Map(
    [hoverState, focusState]
      .map<[string, Point | null] | null>(({ id, labelPosition }) =>
        id ? [id, labelPosition] : null
      )
      .filter(isTruthy)
  );

  if (!higlightedItemsToLabels.size) {
    return {
      renderedDataPoints: dataPoints,
      renderedLabels: labels,
    };
  }

  const datapointsWithInteractiveStates = dataPoints.map(dataPoint =>
    applyInteractiveStatesToDataPoint(higlightedItemsToLabels, dataPoint)
  );

  const chartBounds = new Rect(
    0,
    0,
    dimensions.chart.width,
    dimensions.chart.height
  );

  const labelsForHighlightedDataPoints = datapointsWithInteractiveStates
    .filter(({ isBlurred }) => !isBlurred)
    .flatMap(dataPoint =>
      getLabelsForHighlightedItem({
        highlightDataPoint: dataPoint,
        higlightedItemsToLabels,
        chartBounds,
      })
    );

  return {
    renderedDataPoints: datapointsWithInteractiveStates,
    renderedLabels: labelsForHighlightedDataPoints,
  };
};
